import { type Common } from './common.js';
import { type SuccessResponse } from './models/common.js';
import { type Organization, type UserOrganization } from './models/organization/index.js';
import { type PaginatedQueryOptions } from './models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from './models/pagination/PaginationResponse.type.js';
import { type PracticeWithUserPractice, type UserPractice } from './models/practice.js';
import {
  type PracticeGroupWithUserPracticeGroup,
  type UserPracticeGroup,
} from './models/practiceGroup.js';
import { type ImportValidation } from './models/provider.js';
import { type UserGroupWithDetails } from './models/user/group.js';
import { type PreferenceDetail } from './models/user/preferences.js';
import {
  type ActivateUserArgs,
  type CreateUserResponse,
  type CreateValues,
  type FindAllOptions,
  type ImportRow,
  type MergeProviders,
  type MergeResponse,
  type Role,
  type UpdateValues,
  type User,
  type UserDetails,
  type UserFindAllOptionsPaginated,
  type UserWithDetails,
  type UserWithEhrProviderInformation,
  type UserWithOrganizations,
} from './models/user.js';

export class API {
  common: Common;
  baseEndpoint = '/users';
  authBaseEndpoint = '/auth';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(options: FindAllOptions = {}): Promise<{ users: UserWithOrganizations[] }> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<UserFindAllOptionsPaginated>
  ): Promise<PaginationResponse<UserWithOrganizations>> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async create(values: CreateValues): Promise<CreateUserResponse> {
    const url = this.common.constructPath(`${this.authBaseEndpoint}/create`);
    return await this.common.postJSON(url, values);
  }

  async register(values: {
    password: string;
    repeatedPassword: string;

    // Only required if you're not logged in as an unregistered user
    email?: string;
    lastName?: string;
    registrationCode?: string;
  }): Promise<SuccessResponse> {
    const url = this.common.constructPath(`${this.authBaseEndpoint}/register`);
    return await this.common.postJSON(url, values);
  }

  async findOneByEmail(email: string): Promise<User> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${email}`);
    const { user } = await this.common.getJSON(url);
    return user;
  }

  async findOneById(id: string): Promise<User> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    const { user } = await this.common.getJSON(url);
    return user;
  }

  async findOneByIdWithEhrProviderInformation(id: string): Promise<UserWithEhrProviderInformation> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async findDetailsById(id: string): Promise<UserWithDetails> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/details`);
    return await this.common.getJSON(url);
  }

  async login(
    email: string,
    password: string
  ): Promise<{ success: true; user: User; needs2fa: boolean }> {
    const url = this.common.constructPath(`${this.authBaseEndpoint}/login`);
    return await this.common.postJSON(url, { email, password });
  }

  async getUserDetails(email: string): Promise<UserDetails> {
    const url = this.common.constructPath(`${this.authBaseEndpoint}/userDetails`, { email });
    return await this.common.getJSON(url);
  }

  async logout(): Promise<SuccessResponse> {
    const url = this.common.constructPath(`${this.authBaseEndpoint}/logout`);
    return await this.common.getJSON(url);
  }

  async isLoggedIn(): Promise<boolean> {
    const url = this.common.constructPath(`${this.authBaseEndpoint}/loggedIn`);
    const { isAuthenticated } = await this.common.getJSON(url);
    return isAuthenticated;
  }

  async me(): Promise<UserWithOrganizations> {
    const url = this.common.constructPath(`${this.authBaseEndpoint}/me`);
    const { user } = await this.common.getJSON(url);
    return user;
  }

  async activateProvisionalUser(values: ActivateUserArgs): Promise<CreateUserResponse> {
    const url = this.common.constructPath(`${this.authBaseEndpoint}/activate`);
    return await this.common.postJSON(url, values);
  }

  async update(userId: string, values: UpdateValues): Promise<User> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}`);
    return await this.common.patchJSON(url, values);
  }

  async remove(userId: string): Promise<User> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}`);
    return await this.common.deleteJSON(url);
  }

  async bulkRemove(userIds: string[]): Promise<User[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/bulk/remove`);
    return await this.common.postJSON(url, { userIds });
  }

  async recover(userId: string): Promise<User> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/recover`);
    return await this.common.postJSON(url, {});
  }

  async bulkRecover(userIds: string[]): Promise<User[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/bulk/recover`);
    return await this.common.postJSON(url, { userIds });
  }

  async resetPassword(userId: string): Promise<{ user: User; userRegistrationCode: string }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/reset-password`);
    return await this.common.postJSON(url, {});
  }

  async updateRole(userId: string, role: Role): Promise<User> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/role`);
    return await this.common.patchJSON(url, { role });
  }

  async createPreferences(userId: string, preference: PreferenceDetail): Promise<User> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/preferences`);
    return await this.common.postJSON(url, { preference });
  }

  async updatePreferences(userId: string, preferenceDetail: PreferenceDetail): Promise<User> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/preferences`);
    return await this.common.putJSON(url, { preferenceDetail });
  }

  async findPreferences(userId: string): Promise<PreferenceDetail> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/preferences/full`);
    return await this.common.getJSON(url);
  }

  async findAllGroups(userId: string): Promise<UserGroupWithDetails[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/groups`);
    return await this.common.getJSON(url);
  }

  async findAllOrganizations(userId: string): Promise<Organization[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/organizations`);
    return await this.common.getJSON(url);
  }

  async addOrganization(userId: string, directOrganizationId: string): Promise<Organization> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${userId}/organizations/${directOrganizationId}`
    );
    return await this.common.postJSON(url, {});
  }

  async addOrganizations(userId: string, organizationIds: string[]): Promise<UserOrganization[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/organizations/bulk`);
    return await this.common.postJSON(url, { organizationIds });
  }

  async removeOrganization(userId: string, directOrganizationId: string): Promise<SuccessResponse> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${userId}/organizations/${directOrganizationId}`
    );
    return await this.common.deleteJSON(url);
  }

  async findAllPracticeGroups(userId: string): Promise<PracticeGroupWithUserPracticeGroup[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/practice-groups`);
    return await this.common.getJSON(url);
  }

  async addPracticeGroup(
    userId: string,
    practiceGroupId: string
  ): Promise<PracticeGroupWithUserPracticeGroup> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${userId}/practice-groups/${practiceGroupId}`
    );
    return await this.common.postJSON(url, {});
  }

  async addPracticeGroups(
    userId: string,
    practiceGroupIds: string[]
  ): Promise<UserPracticeGroup[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/practice-groups/bulk`);
    return await this.common.postJSON(url, { practiceGroupIds });
  }

  async removePracticeGroup(userId: string, practiceGroupId: string): Promise<SuccessResponse> {
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${userId}/practice-groups/${practiceGroupId}`
    );
    return await this.common.deleteJSON(url);
  }

  async findAllPractices(userId: string): Promise<PracticeWithUserPractice[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/practices`);
    return await this.common.getJSON(url);
  }

  async addPractice(userId: string, practiceId: string): Promise<PracticeWithUserPractice> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/practices/${practiceId}`);
    return await this.common.postJSON(url, {});
  }

  async addPractices(userId: string, practiceIds: string[]): Promise<UserPractice[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/practices/bulk`);
    return await this.common.postJSON(url, { practiceIds });
  }

  async removePractice(userId: string, practiceId: string): Promise<SuccessResponse> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${userId}/practices/${practiceId}`);
    return await this.common.deleteJSON(url);
  }

  async importVerify(
    values: Record<string, string>[]
  ): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/import/verify`);
    return await this.common.postJSON(url, { providers: values });
  }

  async import(values: ImportRow[]) {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.putJSON(url, { providers: values });
  }

  async merge(mergeProviders: MergeProviders): Promise<MergeResponse> {
    const { userToBeDeletedId, userToBeRetainedId, mergeUpdates } = mergeProviders;
    const url = this.common.constructPath(`${this.baseEndpoint}/merge`);
    return await this.common.postJSON(url, {
      userToBeDeletedId,
      userToBeRetainedId,
      mergeUpdates,
    });
  }
}
